<template>
  <b-card no-body class="mb-1">
    <b-card-header
      header-tag="header"
      class="p-1"
      :class="{'bg-danger text-white': company.companyStatus.id == 900}"
      role="tab"
      v-b-toggle.accordion-1
      v-if="company"
    >
      <div class="header" >
        <div class="title">Company Details - {{ company.companyName }}</div>
        <b-alert v-if="company.doNotCall" class="warning" variant="danger" show>Do Not Call</b-alert>
        <b-alert v-if="company.isCtps" class="warning" variant="danger" show>Marked CTPS</b-alert>
        <div class="status">Status: {{ company.companyStatus.name }}</div>
      </div>
    </b-card-header>
    <b-collapse id="accordion-1" accordion="my-accordion" role="tabpanel">
      <b-card-body v-if="company">
        <b-row class="my-1">
          <b-col md="6">
            <b-row class="my-1">
              <b-col md="6" class="row-title">Registered Name</b-col>
              <b-col md="6">{{company.companyName}}</b-col>
            </b-row>
            <b-row class="my-1">
              <b-col md="6" class="row-title">Trading As</b-col>
              <b-col md="6">{{company.tradingAsName}}</b-col>
            </b-row>
            <b-row class="my-1">
              <b-col md="6" class="row-title">Company Number</b-col>
              <b-col md="6">{{company.registeredNumber}}</b-col>
            </b-row>
            <b-row class="my-1">
              <b-col md="6" class="row-title">Established Since</b-col>
              <b-col md="6">{{company.companyEstablishedYear}}</b-col>
            </b-row>
            <b-row class="my-1">
              <b-col md="6" class="row-title">Tier</b-col>
              <b-col md="6">{{company.companyTier.randdName}}</b-col>
            </b-row>
          </b-col>
          <b-col md="6">
            <b-row class="my-1">
              <b-col md="6" class="row-title"></b-col>
              <b-col md="6">
                <b-button @click="openGlobe" class="margin-right">
                  <font-awesome-icon icon="external-link" /> Globe
                </b-button>
                <b-button @click="openOne80">
                  <font-awesome-icon icon="external-link" /> QD
                </b-button>
              </b-col>
            </b-row>
            <b-row class="my-1">
              <b-col md="6" class="row-title">Business Address</b-col>
              <b-col md="6">
                {{company.addressLine1}} <br/>
                {{company.addressLine2}} <br/>
                {{company.addressLine3}} <br/>
                {{company.addressLine4}} <br/>
                {{company.addressLine5}} <br/>
                {{company.addressLineTown}} <br/>
                {{company.addressLineCounty}}
              </b-col>
            </b-row>
            <b-row class="my-1">
              <b-col md="6" class="row-title">Business Postcode</b-col>
              <b-col md="6">
                {{company.postcode}}
              </b-col>
            </b-row>

          </b-col>
        </b-row>
        <SubHeader title="Financial Details" />
        <b-alert show variant="info" v-if="companyFinancial && companyFinancial.length == 0"><strong>No Financial Details</strong> have been added for this Company</b-alert>
        <b-row class="my-1 small-font" v-if="companyFinancial && companyFinancial.length > 0">
          <b-table
            :items="companyFinancial"
            :fields="fields"
            small striped
          >
            <template v-slot:cell(date)="data">
              {{ data.item.month }}/{{ data.item.year }}
            </template>
            <template v-slot:cell(turnover)="data">
              {{ data.item.turnoverAdjusted | toCurrency(data.item.currencyPrefix) }}
            </template>
            <template v-slot:cell(deposit)="data">
              {{ data.item.bankDepositsAdjusted | toCurrency(data.item.currencyPrefix) }}
            </template>
            <template v-slot:cell(shareholder)="data">
              {{ data.item.shareholdersFundsAdjusted  | toCurrency(data.item.currencyPrefix)}}
            </template>
            <template v-slot:cell(employees)="data">
              {{ data.item.numberOfEmployees }}
            </template>
          </b-table>
        </b-row>

        <SubHeader title="Vendor Details" />
        <b-row class="my-1 small-font">
          <b-table
          :items="companyVendors"
          :fields="contactFields"
          small striped>
            <template v-slot:cell(name)="data">
              <div>
                {{ data.item.fullname || `${data.item.title} ${data.item.firstName} ${data.item.lastName}`}}
                <span v-if="data.item.knownAs"> ({{ data.item.knownAs }})</span>
                <br />
                <span v-if="data.item.shareholding"
                  >Shareholding: {{ data.item.shareholding }}%
                </span>
              </div>
            </template>

            <template v-slot:cell(tel)="data">
              <div
                v-bind:class="{
                  bold: data.item.companyVendorPreferredContactNumberId == 10,
                  red: data.item.doNotUseTelephoneBusiness || data.item.telephoneBusinessCtps,
                }"
                v-if="data.item.telephoneBusiness"
              >
                <font-awesome-icon icon="phone" /> {{ data.item.telephoneBusiness }}
              </div>
              <div
                v-bind:class="{
                  bold: data.item.companyVendorPreferredContactNumberId == 20,
                  red: data.item.doNotUseTelephoneMobile || data.item.telephoneMobileCtps,
                }"
                v-if="data.item.telephoneMobile"
              >
                <font-awesome-icon icon="phone" /> {{ data.item.telephoneMobile }}
              </div>
              <div
                v-bind:class="{
                  bold: data.item.companyVendorPreferredContactNumberId == 30,
                  red: data.item.doNotUseTelephoneAlternative || data.item.telephoneAlternativeCtps,
                }"
                v-if="data.item.telephoneAlternative"
              >
                <font-awesome-icon icon="phone" />
                {{ data.item.telephoneAlternative }}
              </div>
            </template>

            <template v-slot:cell(email)="data">
              <div
                v-bind:class="{ red: data.item.doNotUseEmailAddress }"
                v-if="data.item.emailAddress"
              >
                <font-awesome-icon icon="envelope" /> {{ data.item.emailAddress }}
              </div>
              <div
                v-bind:class="{ red: data.item.doNotUseEmailAddressAlternative }"
                v-if="data.item.emailAddressAlternative"
              >
                <font-awesome-icon icon="envelope" />
                {{ data.item.emailAddressAlternative }}
              </div>
            </template>
          </b-table>
        </b-row>
      </b-card-body>
    </b-collapse>
  </b-card>
</template>

<script>
import SubHeader from "@/components/shared/SubHeader.vue";

export default {
  name: "CompanyHeader",
  props: {
    company: Object,
    companyVendors: Array,
    companyFinancial:Array
  },
  components: {SubHeader},
  data() {
    return {
      fields: [
        { key: "date", label: "Month/Year" },
        { key: "turnover", label: "Turnover" },
        { key: "deposit", label: "Bank & Deposits" },
        { key: "shareholder", label: "Shareholders Funds" },
        { key: "employees", label: "No. of Employees" }
        ],
      contactFields: [
         { key: "name", label: "Vendor & Shareholding" },
         { key: "tel", label: "Telephone" },
         { key: "email", label: "	Email" },
      ]

    }
  },
  methods: {
    openGlobe() {
      window.open( `${process.env.VUE_APP_GLOBE}/CallCompanyDetails.aspx?nocall=1&source=dashboard&id=${this.company.id}`, "_blank");
    },
    openOne80() {
      window.open( `${process.env.VUE_APP_ONE80}/company/${this.company.id}`, "_blank");
    }
  }
};
</script>

<style scoped>
.header {
  display: flex;
}

.status,
.title {

  flex: auto;
  padding: 5px 16px;
  font-size: 1.5em;
  margin: 0;
  cursor: pointer;
  font-weight: 100;
}

.status {
  text-align: right;
}

.warning {
  margin-bottom: 0!important;
}

.row-title {
  font-weight: 500;
  text-align: right;
}

.small-font {
  font-size: 0.9em;
}

</style>
